import { createContext, useContext, useEffect, useState } from "react";
import Medusa from "@medusajs/medusa-js";
import { server_url, WEBSITE_REGION_NAME } from "../../environmentVariables/env";
import useLocalStorage from "../../hooks/useLocalStorage";
import { test_users } from "../../environmentVariables/env";

const medusa_baseUrl = server_url;

const MedusaContext = createContext();


export const useMedusa = () => useContext(MedusaContext);


export const MedusaProvider = ({ children }) => {
    const [isTestingActive, setTesting] = useState(false);

    const [medusa, setMedusa] = useState(null);
    const [region, setRegion] = useState(null);
    const [user, setUser] = useState(null);
    const [cart, setCart] = useState(null);
    const [cartId, setCartId] = useLocalStorage("cart_id", "");
    const [wishlist, setWishlist] = useState([]);
    const [swapActive, setSwapActive] = useState(false);
    const [swap, setSwap] = useState({
        order_id: "",
        form_details: {
            reason: {},
            condition: {},
            outcome: {},
            additional_info: "",
            file: null,
            items: []
        },
        return_items: [],
        additional_items: [],
    });
    const [swapCart, setSwapCart] = useState([]);

    const [shoppingTags, setShoppingTags] = useState([]);

    // cart modal settings
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (medusa != null) return;
        initializeClient();
    }, []);

    useEffect(() => {
        if (!medusa) return;
        initializeRegion();
        refetchUser();
    }, [medusa]);

    useEffect(() => {
        if (!user) {
            setWishlist(() => null)
        } else {
            // Check if testing active
            if (test_users.includes(user.email)) {
                setTesting(true);
            } else {
                setTesting(false);
            }

            // Set Wishlist
            setWishlist(() =>
                user.metadata?.wishlist
            );
        }
    }, [user]);

    const initializeClient = async () => {
        try {
            const medusaInstance = new Medusa({
                maxRetries: 3,
                baseUrl: medusa_baseUrl,
            })
            setMedusa(() => medusaInstance);
        } catch (e) {
            console.error(e);
        }
    };

    const initializeRegion = async () => {
        try {
            await medusa.regions.list()
                .then(({ regions }) => {
                    // console.log(regions);
                    setRegion(() =>
                        regions.find(reg =>
                            reg.name.toLowerCase() === WEBSITE_REGION_NAME.toLowerCase()
                        )
                    )
                });

        } catch (e) {
            console.error(e);
        }
    }

    const refetchUser = async () => {
        try {
            await medusa.auth.getSession()
                .then(({ customer }) => {
                    setUser(customer)
                })
                .catch(err => err)
        } catch (e) {
            console.error(e);
        }
    }

    useEffect(() => {
        if (!medusa) return;
        initializeCart();
    }, [medusa, user])

    const initializeCart = async () => {
        // Early return if cart is already properly initialized
        if (user && cart && (user?.metadata?.cart_id === cart?.id) && (user?.email === cart?.email)) {
            return;
        }

        try {
            // Check if user exists
            if (user) {
                // HANDLE LOGGED IN USER
                setCartId(""); // Clear any existing cart ID from local storage

                // User is associated with an active cart
                if (user.metadata?.cart_id) {
                    // Fetch cart with the user.metadata.cart_id
                    let _cart = await medusa.carts.retrieve(user.metadata.cart_id)
                        .then(({ cart: _c }) => _c)
                        .catch(() => null);

                    // If cart doesn't exist or is completed, create a new one
                    if (!_cart || _cart.completed_at) {
                        _cart = await medusa.carts.create()
                            .then(({ cart: _c }) => _c);

                        await medusa.customers.update({
                            metadata: { cart_id: _cart.id }
                        });
                        refetchUser(); // Moved outside the then chain for better readability
                    }
                    setCart(_cart);
                } else {
                    // User is not associated with an active cart
                    const newCart = await medusa.carts.create()
                        .then(({ cart: _cart }) => _cart);

                    await medusa.customers.update({
                        metadata: { cart_id: newCart.id }
                    });
                    refetchUser(); // Moved outside the then chain
                    setCart(newCart);
                }
            } else {
                // HANDLE NO USER LOGGED IN
                if (cartId) {
                    let fetchedCart;
                    try {
                        fetchedCart = await medusa.carts.retrieve(cartId)
                            .then(({ cart: _c }) => _c);
                    } catch (error) {
                        // If retrieval fails (cart not found), create new one
                        fetchedCart = null;
                    }

                    if (!fetchedCart || fetchedCart.completed_at) {
                        const newCart = await medusa.carts.create()
                            .then(({ cart: _cart }) => _cart);
                        setCart(newCart);
                        setCartId(newCart.id);
                    } else {
                        setCart(fetchedCart);
                    }
                } else {
                    const newCart = await medusa.carts.create()
                        .then(({ cart: _cart }) => _cart);
                    setCart(newCart);
                    setCartId(newCart.id);
                }
            }
        } catch (e) {
            console.error("Error initializing cart:", e);
            // Consider adding error handling/recovery here
        }
    };

    // // Testing
    // useEffect(() => {
    //     console.log("CART/////////////", cart)
    // }, [cart])

    // useEffect(() => {
    //     console.log("REGION/////////////", region, WEBSITE_REGION_NAME);
    // }, [region])

    // useEffect(() => {
    //     console.log("USER/////////", user)
    // }, [user]);

    // useEffect(() => {
    //     console.log(wishlist);
    // }, [wishlist])

    // useEffect(() => {
    //     console.log(wishlist);
    // }, [wishlist])

    let value = {
        isTestingActive,
        medusa,
        region,
        user,
        setUser: (_user) => setUser(_user),
        cart,
        setCart: (_cart) => setCart(_cart),
        setCartIdLocal: (_id) => setCartId(_id),
        wishlist,
        setWishlist: (_wishlist) => setWishlist(_wishlist),
        swap,
        setSwap: (val) => setSwap(val),
        swapActive,
        setSwapActive: (flag) => setSwapActive(flag),
        swapCart,
        setSwapCart: (_cart) => setSwapCart(_cart),

        // cart modal
        open,
        setOpen: (flag) => setOpen(flag),
    };

    return (
        <MedusaContext.Provider value={value} >
            {children}
        </MedusaContext.Provider>
    )
}